import { useEffect, useRef, useCallback, useState } from "react";
import api from "../lib/api";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

function isSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

// Light "ding" using Web Audio API. No external assets, instant playback.
let audioCtx = null;
function playChime() {
  try {
    if (!audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      audioCtx = new Ctx();
    }
    if (audioCtx.state === "suspended") audioCtx.resume().catch(() => {});
    const now = audioCtx.currentTime;
    const playTone = (freq, start, dur, gain = 0.18) => {
      const osc = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0, now + start);
      g.gain.linearRampToValueAtTime(gain, now + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(g);
      g.connect(audioCtx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    };
    // Two-tone chime
    playTone(880, 0, 0.28);
    playTone(1320, 0.18, 0.32);
  } catch (e) {
    // Ignore audio failures
  }
}

export function usePushNotifications(userId, userRole) {
  const subscribedRef = useRef(false);
  const [permission, setPermission] = useState(
    isSupported() ? Notification.permission : "unsupported"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [supported] = useState(isSupported());
  const [badgeCount, setBadgeCount] = useState(0);

  const ensureRegistered = useCallback(async () => {
    if (!isSupported()) return null;
    let reg = await navigator.serviceWorker.getRegistration();
    if (!reg) reg = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;
    return reg;
  }, []);

  const enable = useCallback(async () => {
    if (!isSupported() || !VAPID_PUBLIC_KEY || !userId) return { ok: false, reason: "unsupported" };
    try {
      const reg = await ensureRegistered();
      if (!reg) return { ok: false, reason: "no-registration" };
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return { ok: false, reason: "denied" };
      // Reuse existing subscription if present
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
      }
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      subscribedRef.current = true;
      setSubscribed(true);
      return { ok: true };
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("Push enable failed:", e);
      return { ok: false, reason: e.message || "error" };
    }
  }, [ensureRegistered, userId]);

  const disable = useCallback(async () => {
    if (!isSupported()) return { ok: false };
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe();
      }
      subscribedRef.current = false;
      setSubscribed(false);
      return { ok: true };
    } catch (e) {
      return { ok: false };
    }
  }, []);

  // On mount: detect existing subscription state, register SW
  useEffect(() => {
    if (!isSupported() || !userId) return;
    (async () => {
      try {
        const reg = await ensureRegistered();
        if (!reg) return;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          setSubscribed(true);
          subscribedRef.current = true;
          // Re-sync subscription with backend (idempotent)
          api.post("/push/subscribe", { subscription: sub.toJSON() }).catch(() => {});
        }
      } catch (e) {
        // ignore
      }
    })();
  }, [userId, ensureRegistered]);

  // Listen to SW postMessage for audio + navigation + badge
  useEffect(() => {
    if (!isSupported()) return;
    const onMessage = (event) => {
      const msg = event.data || {};
      if (msg.kind === "notification:play-sound") {
        // Use the loud booking ringtone for new bookings, short chime otherwise.
        if (msg.type === "new_booking" && typeof window !== "undefined" && typeof window.__trigoPlayBookingRingtone === "function") {
          window.__trigoPlayBookingRingtone();
        } else {
          playChime();
        }
      } else if (msg.kind === "notification:click" && msg.url) {
        if (typeof window !== "undefined" && typeof window.__trigoStopBookingRingtone === "function") {
          window.__trigoStopBookingRingtone();
        }
        try {
          window.location.href = msg.url;
        } catch (e) { /* noop */ }
      } else if (msg.kind === "badge:update" && typeof msg.count === "number") {
        setBadgeCount(msg.count);
      } else if (msg.kind === "badge:clear") {
        setBadgeCount(0);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, []);

  const clearBadge = useCallback(() => {
    setBadgeCount(0);
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ kind: "badge:clear" });
    }
  }, []);

  return {
    supported,
    permission,
    subscribed,
    badgeCount,
    enable,
    disable,
    clearBadge,
    playChime,
    // legacy
    subscribe: enable,
  };
}
